import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { calculatePercentage } from './utils/calculatePercentage';
import { RESPIRATION_MAX_DISPLAY_BRPM } from '../../utilities/respirationDisplay';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    width: 500,
  },
  cell: {
    border: 'none',
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
}));

type SummaryHrvMetrics = {
  sdnn: number | null;
  rmssd: number | null;
  meanRr: number | null;
  stressIndex: number | null;
  pnn50: number | null;
  nnCount?: number | null;
} | null;

type SummaryRespirationMetrics = {
  medianRrBrpm: number | null;
  coveragePercent: number | null;
} | null;

type SummaryApneaMetrics = {
  assessable: boolean;
  burdenMinPerH: number | null;
  analysedHours: number | null;
  afExcludedMinutes: number | null;
} | null;

type SummaryRow = {
  name: string;
  count: string | number;
  percentages: string | number;
};

function createData(name: string, count: SummaryRow['count'], percentages: SummaryRow['percentages']): SummaryRow {
  return { name, count, percentages };
}

function makeRows(
  {
    avgHeartrate,
    beatCount,
    minHeartrate,
    maxHeartrate,
    supraventricularBeats,
    ventricularBeats,
    normalBeats,
    bradycardiaBeats,
    tachycardiaBeats,
    averageRatesBeats,
    qtcIntervalMin,
    qtcIntervalMax,
    qtcIntervalAverage,
    qtcIntervalsAbove05Percentage,
    pauseTotalCount,
    pauseLongestEpisodeSeconds,
    hrv,
    respiration,
    apnea,
  }: SummeryProps,
  t: TFunction<'translation'>,
): SummaryRow[] {
  const formatNumber = (value: number | null | undefined, fractionDigits = 1) =>
    value == null || Number.isNaN(value) ? '—' : value.toFixed(fractionDigits);

  const formatPercentageValue = (value: number | null | undefined, fractionDigits = 2) =>
    value == null || Number.isNaN(value) ? '—' : `${value.toFixed(fractionDigits)} %`;

  const rows: SummaryRow[] = [
    createData(t('summary.rows.minMaxHeartrate'), `${minHeartrate || 0}/${maxHeartrate || 0}`, ''),
    createData(t('summary.rows.averageHeartrate'), avgHeartrate || 0, ''),
    createData(t('summary.rows.totalBeats'), beatCount || 0, '100 %'),
    createData(
      t('summary.rows.bradycardiaBeats', { bpm: t('common.below50bpm') }),
      '',
      calculatePercentage(bradycardiaBeats, averageRatesBeats),
    ),
    createData(
      t('summary.rows.tachycardiaBeats', { bpm: t('common.above100bpm') }),
      '',
      calculatePercentage(tachycardiaBeats, averageRatesBeats),
    ),
    createData(
      t('summary.rows.supraventricularBeats'),
      supraventricularBeats,
      calculatePercentage(supraventricularBeats, beatCount),
    ),
    createData(
      t('summary.rows.ventricularBeats'),
      ventricularBeats,
      calculatePercentage(ventricularBeats, beatCount),
    ),
    createData(t('summary.rows.normalBeats'), normalBeats, ''),
    createData(
      t('summary.rows.qtcIntervalMinMax'),
      `${qtcIntervalMin.toFixed(3)} / ${qtcIntervalMax.toFixed(3)}`,
      '',
    ),
    createData(t('summary.rows.qtcIntervalAverage'), qtcIntervalAverage.toFixed(3), ''),
    createData(
      t('summary.rows.qtcIntervalsAboveThreshold'),
      '',
      `${qtcIntervalsAbove05Percentage.toFixed(2)}%`,
    ),
    createData(t('summary.rows.pauseTotalCount'), pauseTotalCount, ''),
    createData(t('summary.rows.pauseLongestEpisode'), pauseLongestEpisodeSeconds, ''),
  ];

  if (hrv) {
    rows.push(
      createData(t('summary.rows.hrvSdnn'), formatNumber(hrv.sdnn), ''),
      createData(t('summary.rows.hrvRmssd'), formatNumber(hrv.rmssd), ''),
      createData(t('summary.rows.hrvMeanRr'), formatNumber(hrv.meanRr), ''),
      createData(t('summary.rows.hrvStressIndex'), formatNumber(hrv.stressIndex, 2), ''),
      createData(t('summary.rows.hrvPnn50'), '', formatPercentageValue(hrv.pnn50)),
    );
  }

  // Respiration is measured over part of the recording, so the rate is only
  // meaningful next to its coverage — the two rows are added together or not
  // at all. Both describe the validated-range population (#549).
  if (respiration && respiration.medianRrBrpm != null) {
    const max = RESPIRATION_MAX_DISPLAY_BRPM;

    rows.push(
      createData(
        t('summary.rows.respirationRate', { max }),
        formatNumber(respiration.medianRrBrpm),
        '',
      ),
      createData(
        t('summary.rows.respirationCoverage', { max }),
        '',
        formatPercentageValue(respiration.coveragePercent, 0),
      ),
    );
  }

  // Sleep-disordered breathing screening. Burden and the hours it was
  // measured over travel together; AF-excluded minutes only when there were
  // any. Nothing is printed for a non-assessable screen — the card itself
  // explains why.
  if (apnea && apnea.assessable && apnea.burdenMinPerH != null && apnea.analysedHours != null) {
    rows.push(
      createData(t('summary.rows.apneaBurden'), formatNumber(apnea.burdenMinPerH), ''),
      createData(t('summary.rows.apneaHoursAnalysed'), formatNumber(apnea.analysedHours), ''),
    );

    if (apnea.afExcludedMinutes != null && apnea.afExcludedMinutes > 0) {
      rows.push(
        createData(t('summary.rows.apneaAfExcluded'), Math.round(apnea.afExcludedMinutes), ''),
      );
    }
  }

  return rows;
}

type SummeryProps = {
  avgHeartrate: number;
  beatCount: number;
  minHeartrate: number;
  maxHeartrate: number;
  supraventricularBeats: number;
  ventricularBeats: number;
  normalBeats: number;
  bradycardiaBeats: number;
  tachycardiaBeats: number;
  averageRatesBeats: number;
  qtcIntervalMin: number;
  qtcIntervalMax: number;
  qtcIntervalAverage: number;
  qtcIntervalsAbove05Percentage: number;
  pauseTotalCount: number;
  pauseLongestEpisodeSeconds: number;
  hrv?: SummaryHrvMetrics;
  respiration?: SummaryRespirationMetrics;
  apnea?: SummaryApneaMetrics;
};

export function Summary(props: { summary: SummeryProps }) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Container className={classes.root} component={Paper}>
      <Typography className={classes.title} variant="h6">
        {t('summary.title')}
      </Typography>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('summary.table.name')}</TableCell>
            <TableCell align="right">{t('summary.table.count')}</TableCell>
            <TableCell align="right">{t('common.percentages')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {makeRows(props.summary, t).map((row) => (
            <TableRow key={row.name}>
              <TableCell className={classes.cell}>{row.name}</TableCell>
              <TableCell className={classes.cell} align="right">
                {row.count}
              </TableCell>
              <TableCell className={classes.cell} align="right">
                {row.percentages}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Container>
  );
}
